import React, { useCallback, useState } from 'react';
import { Folder, FolderInput } from 'lucide-react';
import MoveToFolderMenu from './MoveToFolderMenu';
import { logUiEvent } from '../../services/uiLogger';

// Удавшийся перенос и новую папку пишем тем же событием, что боковая панель:
// по нему считается, сколько людей пользуются папками.
const logFolder = (action) => logUiEvent('history_folder', { component: 'history', meta: { action } });

/**
 * Путь к открытой записи: «История / 📁 Папка» и значок «переложить». Стоит в
 * верхней строке экрана (на телефоне — маленькой строкой над названием записи).
 * «История» ведёт ко всем записям, папка — открывает её в списке слева; запись
 * вне папок (или папка не найдена среди известных) подписана «без папки».
 * Перенос делает приложение: отказ onMove/onCreateAndMove показываем здесь же.
 */
export default function FolderPath({
  folders, folderId, onOpenAll, onOpenFolder, onMove, onCreateAndMove, compact = false,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState('');
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const folder = folderId ? (folders || []).find((f) => f.id === folderId) : null;

  const move = async (fid) => {
    setMenuOpen(false);
    setError('');
    try {
      await onMove(fid);
    } catch (err) {
      setError(err?.message || 'Не удалось переложить запись');
      return;
    }
    // Выбор той же папки (строка с ✓) — не перенос.
    if (fid !== (folderId || null)) logFolder('move');
  };

  // Ошибку создания показывает само меню (поле «Новая папка…»); закрываем его
  // только когда перенос удался.
  const createAndMove = async (name) => {
    setError('');
    await onCreateAndMove(name);
    logFolder('create');
    logFolder('move');
    setMenuOpen(false);
  };

  const link = 'shrink-0 text-paper-ink-3 hover:text-paper-ink hover:underline';
  const triggerLabel = folder ? 'Переложить в другую папку' : 'Положить в папку';

  return (
    <div data-testid="folder-path" className={`relative flex min-w-0 items-center gap-[6px] whitespace-nowrap ${compact ? 'text-[12px]' : 'text-[13px]'}`}>
      <button type="button" onClick={onOpenAll} className={link}>История</button>
      <span aria-hidden="true" className="text-paper-hint">/</span>
      {folder ? (
        <button
          type="button"
          title="Открыть папку в списке"
          onClick={() => onOpenFolder(folder.id)}
          className="flex min-w-0 items-center gap-1 font-semibold text-paper-ink hover:underline"
        >
          <Folder className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{folder.name}</span>
        </button>
      ) : (
        <span className="text-paper-ink-3">без папки</span>
      )}
      <button
        type="button"
        title={triggerLabel}
        aria-label={triggerLabel}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
        // Меню закрывается по mousedown вне него: без этого клик по значку
        // закрывал бы меню на mousedown и тут же открывал заново на click.
        onMouseDown={(e) => e.stopPropagation()}
        className={`${compact ? 'flex h-9 w-9 items-center justify-center -my-2' : 'p-[2px]'} shrink-0 text-paper-hint hover:text-paper-ink`}
      >
        <FolderInput className="h-3.5 w-3.5" />
      </button>
      {error && <span role="alert" className="shrink-0 text-paper-accent">{error}</span>}
      {menuOpen && (
        <div className="absolute left-0 top-full z-30 mt-1">
          <MoveToFolderMenu
            folders={folders}
            currentId={folderId || null}
            onMove={move}
            onCreateAndMove={createAndMove}
            onClose={closeMenu}
          />
        </div>
      )}
    </div>
  );
}
